// src/components/dealer/DealerListings.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createConsumer } from '@rails/actioncable';
import { dealerAPI } from '../../services/dealerAPI';

const fmt = (val) => (val ? `₹${Number(val).toLocaleString('en-IN')}` : '—');

/** Format a Date to "h:mm AM/PM" */
const fmtTime = (date) => {
  if (!date) return '—';
  return date.toLocaleTimeString('en-IN', { hour:'numeric', minute:'2-digit', hour12:true });
};

/** True when bidding is finished */
const isBiddingClosed = (listing) =>
  listing?.dealer_status  === 'deal_finalized' ||
  listing?.bidding_status === 'closed'         ||
  listing?.deal_closed_at != null              ||
  (listing?.bidding_end_at && new Date(listing.bidding_end_at) < new Date());

/**
 * Bid Window range string.
 * Start = bidding_start_at
 * End   = deal_closed_at (real acceptance time) OR bidding_end_at fallback
 */
const getBidRange = (listing) => {
  const start  = listing?.bidding_start_at ? new Date(listing.bidding_start_at) : null;
  const endRaw = listing?.deal_closed_at || listing?.bidding_end_at;
  const end    = endRaw ? new Date(endRaw) : null;
  if (!start || !end) return null;
  return `${fmtTime(start)} – ${fmtTime(end)}`;
};

/** Rough countdown for active listings card footer */
const timeLeft = (endAt) => {
  if (!endAt) return null;
  const ms = new Date(endAt) - Date.now();
  if (ms <= 0) return 'Ended';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const DealerListings = () => {
  const navigate = useNavigate();
  const [listings,  setListings]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [liveStats, setLiveStats] = useState({});
  const [flashIds,  setFlashIds]  = useState(new Set());
  // Tracks listings closed via WebSocket (with their deal_closed_at)
  const [closedMap, setClosedMap] = useState({}); // { listingId: deal_closed_at_iso }
  const subsRef     = useRef({});
  const consumerRef = useRef(null);

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await dealerAPI.getListings();
      const list = res.data?.data || res.data || [];
      setListings(list);

      const statsMap  = {};
      const closedInit = {};
      list.forEach(l => {
        statsMap[l.id] = { highest:l.highest_offer_price, total:l.total_offers_count||0 };
        if (isBiddingClosed(l)) {
          closedInit[l.id] = l.deal_closed_at || l.bidding_end_at || true;
        }
      });
      setLiveStats(statsMap);
      setClosedMap(prev => ({ ...closedInit, ...prev })); // preserve WS-updated times
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load listings.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    if (!listings.length) return;

    const token  = localStorage.getItem('dealerToken') || localStorage.getItem('authToken');
    const wsBase = process.env.REACT_APP_WS_URL || 'ws://localhost:3001/cable';
    const wsUrl  = token ? `${wsBase}?token=${token}` : wsBase;

    if (consumerRef.current) {
      Object.values(subsRef.current).forEach(sub => sub.unsubscribe());
      consumerRef.current.disconnect();
      subsRef.current = {};
    }

    const consumer      = createConsumer(wsUrl);
    consumerRef.current = consumer;

    listings.forEach(listing => {
      if (subsRef.current[listing.id]) return;

      subsRef.current[listing.id] = consumer.subscriptions.create(
        { channel:'DealerBiddingChannel', listing_id:listing.id },
        {
          connected() { console.log(`[Cable] Connected listing ${listing.id}`); },

          received(data) {
            if (data.type === 'SNAPSHOT' || data.type === 'NEW_BID') {
              setLiveStats(prev => ({ ...prev, [listing.id]:{ highest:data.highest_price, total:data.total_offers } }));
              if (data.type === 'NEW_BID') {
                setFlashIds(prev => { const n=new Set(prev); n.add(listing.id); return n; });
                setTimeout(() => setFlashIds(prev => { const n=new Set(prev); n.delete(listing.id); return n; }), 1500);
              }
            }

            if (data.type === 'BID_RESULT') {
              // Store the exact close timestamp from the broadcast
              const closedAt = data.deal_closed_at || new Date().toISOString();
              setClosedMap(prev => ({ ...prev, [listing.id]: closedAt }));
              fetchListings();
            }
          },
        }
      );
    });

    return () => {
      Object.values(subsRef.current).forEach(sub => sub.unsubscribe());
      subsRef.current = {};
      consumer.disconnect();
      consumerRef.current = null;
    };
  }, [listings, fetchListings]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'50vh', gap:16, flexDirection:'column' }}>
      <div style={{ width:48, height:48, border:'5px solid #e0e0e0', borderTopColor:'#003566', borderRadius:'50%', animation:'spin 0.9s linear infinite' }}/>
      <p style={{ color:'#666', fontWeight:600 }}>Loading listings…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding:40, textAlign:'center' }}>
      <p style={{ color:'#dc3545', fontWeight:700 }}>⚠️ {error}</p>
      <button onClick={fetchListings} style={{ marginTop:12, padding:'10px 24px', background:'#003566', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700 }}>Retry</button>
    </div>
  );

  return (
    <div style={{ padding:24 }}>
      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes flash { 0%{box-shadow:0 0 0 4px #FFD60A80}100%{box-shadow:none} }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 3px #28a74530}50%{box-shadow:0 0 0 6px #28a74515} }
      `}</style>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:28, fontWeight:800, color:'#003566' }}>🚘 Cars Bidding</h1>
          <p style={{ margin:0, color:'#666', fontSize:14 }}>{listings.length} car{listings.length!==1?'s':''} — live updates active</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ width:10, height:10, borderRadius:'50%', background:'#28a745', boxShadow:'0 0 0 4px #28a74530', animation:'pulse 1.5s infinite', display:'inline-block' }}/>
          <span style={{ fontWeight:700, color:'#28a745', fontSize:13 }}>LIVE</span>
        </div>
      </div>

      {listings.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px', background:'#f5f5f5', borderRadius:16 }}>
          <p style={{ fontSize:40, margin:'0 0 12px' }}>📭</p>
          <p style={{ fontSize:16, fontWeight:700, color:'#333', margin:'0 0 6px' }}>No active listings</p>
          <p style={{ fontSize:13, color:'#999', margin:0 }}>New car listings will appear here when admin sends them.</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:20 }}>
          {listings.map(listing => {
            const stats   = liveStats[listing.id] || {};
            const isFlash = flashIds.has(listing.id);

            // closed = from WS closedMap OR from listing data
            const wsClosed   = listing.id in closedMap;
            const closed     = wsClosed || isBiddingClosed(listing);

            // Build a merged listing object that includes WS-updated deal_closed_at
            const mergedListing = wsClosed
              ? { ...listing, deal_closed_at: closedMap[listing.id], bidding_status:'closed', dealer_status:'deal_finalized' }
              : listing;

            const tl        = !closed ? timeLeft(listing.bidding_end_at) : null;
            const timeRange = closed ? getBidRange(mergedListing) : null;

            return (
              <div
                key={listing.id}
                onClick={() => navigate(`/dealer/listings/${listing.id}`)}
                style={{
                  background:'white',
                  border: isFlash ? '2px solid #FFD60A' : closed ? '2px solid #dc354540' : '2px solid #e0e0e0',
                  borderRadius:16, padding:'20px 22px',
                  cursor:'pointer', transition:'all 0.25s ease',
                  animation: isFlash ? 'flash 0.8s ease' : 'none',
                  boxShadow: isFlash ? '0 0 20px rgba(255,214,10,0.3)' : '0 2px 8px rgba(0,0,0,0.06)',
                  opacity: closed ? 0.88 : 1,
                }}
                onMouseEnter={e => { if (!isFlash) e.currentTarget.style.borderColor = closed?'#dc3545':'#003566'; e.currentTarget.style.transform='translateY(-3px)'; }}
                onMouseLeave={e => { if (!isFlash) e.currentTarget.style.borderColor = closed?'#dc354540':'#e0e0e0'; e.currentTarget.style.transform='translateY(0)'; }}
              >
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                    <h3 style={{ margin:'0 0 4px', fontSize:17, fontWeight:800, color:'#003566' }}>
                      {listing.car?.brand} {listing.car?.model} {listing.car?.year}
                    </h3>
                    <span style={{
                      background: closed?'#dc354520':'#28a74520',
                      color:      closed?'#dc3545':'#28a745',
                      border:     `1px solid ${closed?'#dc354540':'#28a74540'}`,
                      fontSize:10, fontWeight:800, padding:'3px 8px', borderRadius:5,
                      textTransform:'uppercase', letterSpacing:0.5, flexShrink:0,
                    }}>
                      {closed ? 'Closed' : 'Active'}
                    </span>
                  </div>
                  <p style={{ margin:0, fontSize:13, color:'#666' }}>
                    {listing.car?.city} · {listing.car?.km_driven?.toLocaleString('en-IN')} km
                  </p>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
                  <div style={{ background:'linear-gradient(135deg,#003566,#001d3d)', borderRadius:10, padding:'12px 14px' }}>
                    <p style={{ margin:'0 0 2px', fontSize:10, fontWeight:700, color:'rgba(255,214,10,0.8)', textTransform:'uppercase' }}>
                      {closed ? 'Final Bid' : 'Highest Bid'}
                    </p>
                    <p style={{ margin:0, fontSize:17, fontWeight:800, color:'#FFD60A' }}>{fmt(stats.highest) || '—'}</p>
                  </div>
                  <div style={{ background:'#f8f9fa', borderRadius:10, padding:'12px 14px', border:'1px solid #e0e0e0' }}>
                    <p style={{ margin:'0 0 2px', fontSize:10, fontWeight:700, color:'#999', textTransform:'uppercase' }}>Total Bids</p>
                    <p style={{ margin:0, fontSize:17, fontWeight:800, color:'#003566' }}>{stats.total || 0}</p>
                  </div>
                </div>

                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:12 }}>
                  <span style={{ color:'#666' }}>Range: {fmt(listing.price_range_min)} – {fmt(listing.price_range_max)}</span>
                  {/* Closed → static time range (start → actual close). Active → live countdown */}
                  {closed && timeRange ? (
                    <span style={{ fontWeight:700, fontFamily:'monospace', color:'#dc3545', fontSize:11 }}>⏹ {timeRange}</span>
                  ) : tl ? (
                    <span style={{ fontWeight:700, fontFamily:'monospace', color:tl==='Ended'?'#dc3545':(tl.includes('m')&&!tl.includes('h'))?'#f39c12':'#28a745' }}>⏱ {tl}</span>
                  ) : null}
                </div>

                {!closed ? (
                  <div style={{ marginTop:16 }}>
                    <div style={{ background:'linear-gradient(135deg,#FFD60A,#FFC107)', color:'#003566', fontWeight:800, fontSize:14, padding:11, borderRadius:10, textAlign:'center' }}>
                      🔨 Place Bid
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop:16, background:'#dc354510', border:'1px solid #dc354530', borderRadius:8, padding:'8px 12px', textAlign:'center' }}>
                    <span style={{ fontSize:12, color:'#dc3545', fontWeight:700 }}>🔒 Bidding Closed</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DealerListings;